import { useEffect, useState } from "react";
import { Thermometer, Flame, Snowflake, Clock, ChefHat } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";

const DEFAULTS = {
  cook_min_c: 75,
  reheat_min_c: 75,
  hot_hold_min_c: 63,
  hot_hold_max_hours: 4,
  cold_hold_max_c: 4,
  cold_hold_max_hours: 2,
  cooling_max_hours: 4,
  cooling_end_max_c: 4,
};

const FIELDS = [
  { group: "cooking", key: "cook_min_c", label: "Cook — minimum core temperature", unit: "°C", icon: Flame, color: "#dc2626", hint: "Food must be cooked to at least this temperature." },
  { group: "cooking", key: "reheat_min_c", label: "Reheat — minimum temperature", unit: "°C", icon: Flame, color: "#dc2626", hint: "Reheated food must reach at least this temperature (reheat allowed once)." },
  { group: "cooking", key: "cooling_max_hours", label: "Cooling — maximum duration", unit: "hrs", icon: Clock, color: "#2563eb", hint: "Cook-chill must complete within this window." },
  { group: "cooking", key: "cooling_end_max_c", label: "Cooling — target end temperature", unit: "°C", icon: Snowflake, color: "#2563eb", hint: "Food must be cooled down to at or below this temperature." },
  { group: "holding", key: "hot_hold_min_c", label: "Hot holding — minimum temperature", unit: "°C", icon: Flame, color: "#dc2626", hint: "Hot-held food must stay at or above this temperature." },
  { group: "holding", key: "hot_hold_max_hours", label: "Hot holding — maximum duration", unit: "hrs", icon: Clock, color: "#dc2626", hint: "Maximum time food may be hot-held before it must be served or discarded." },
  { group: "holding", key: "cold_hold_max_c", label: "Cold holding — maximum temperature", unit: "°C", icon: Snowflake, color: "#2563eb", hint: "Cold-held food must stay at or below this temperature." },
  { group: "holding", key: "cold_hold_max_hours", label: "Cold holding — maximum duration", unit: "hrs", icon: Clock, color: "#2563eb", hint: "Maximum time food may be cold-held before it must be served or discarded." },
];

function FieldRow({ f, value, onChange }) {
  const Icon = f.icon;
  return (
    <div className="flex items-start gap-3">
      <Icon size={18} style={{ color: f.color }} className="mt-1.5 shrink-0" />
      <div className="flex-1">
        <label className="text-xs uppercase font-semibold opacity-70">{f.label}</label>
        <div className="flex items-center gap-2 mt-1">
          <input className="vb-input" type="number" step="0.1" value={value} onChange={onChange} data-testid={`hch-cfg-${f.key}`} />
          <span className="text-sm opacity-60 w-10">{f.unit}</span>
        </div>
        <p className="text-[11px] opacity-60 mt-1">{f.hint}</p>
      </div>
    </div>
  );
}

export default function AdminHotColdHold() {
  const [config, setConfig] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/add-ons/hot-cold-hold/config");
      if (data.config && Object.keys(data.config).length) {
        setConfig((prev) => ({ ...prev, ...data.config }));
      }
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.put("/admin/add-ons/hot-cold-hold/config", { config });
      toast.success("Hot & Cold Hold thresholds saved — applied to every tenant with the add-on enabled.");
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to save"); }
    finally { setBusy(false); }
  };

  const num = (k) => (e) => setConfig({ ...config, [k]: Number(e.target.value) });

  return (
    <div className="vb-fade-in" data-testid="admin-hot-cold-hold-page">
      <div className="flex items-center gap-3 mb-2">
        <Thermometer size={26} style={{ color: "var(--vb-accent)" }} />
        <h1 className="vb-heading text-3xl" style={{ color: "var(--vb-text-2)" }}>Daily Kitchen Record</h1>
      </div>
      <p className="opacity-70 mb-6 max-w-3xl">
        Platform-wide <strong>critical limits</strong> for the Daily Kitchen Record add-on — covering both the
        Cooking Record (cook, cool &amp; store, reheat) and the Hot &amp; Cold Hold log. These apply to every
        tenant with the add-on enabled. A record that breaches any limit is flagged as a non-conformance and
        requires a corrective action.
      </p>

      {err && <div className="vb-pill err mb-4" data-testid="hch-config-error">{err}</div>}

      <div className="vb-card p-6 mb-5 max-w-3xl" data-testid="hch-card-cooking">
        <div className="flex items-center gap-2 mb-4">
          <ChefHat size={18} style={{ color: "var(--vb-accent)" }} />
          <h2 className="m-0 text-lg" style={{ color: "var(--vb-text-2)" }}>Cooking &amp; reheating</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          {FIELDS.filter((f) => f.group === "cooking").map((f) => (
            <FieldRow key={f.key} f={f} value={config[f.key]} onChange={num(f.key)} />
          ))}
        </div>
      </div>

      <div className="vb-card p-6 mb-6 max-w-3xl" data-testid="hch-card-holding">
        <div className="flex items-center gap-2 mb-4">
          <Thermometer size={18} style={{ color: "var(--vb-accent)" }} />
          <h2 className="m-0 text-lg" style={{ color: "var(--vb-text-2)" }}>Hot &amp; cold holding</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          {FIELDS.filter((f) => f.group === "holding").map((f) => (
            <FieldRow key={f.key} f={f} value={config[f.key]} onChange={num(f.key)} />
          ))}
        </div>
      </div>

      <button className="vb-btn" onClick={save} disabled={busy} data-testid="hch-config-save">
        {busy ? "Saving…" : "Save thresholds"}
      </button>
    </div>
  );
}
